// useToggleAction.js
import { useState, useEffect } from 'react';
import { collection, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';

/**
 * Toggle a per-user flag (e.g., "liked" or "favorite") under either:
 *
 * 1) A specific document:
 *    collectionPath/<documentId>/<actionType>/<userId>
 *
 * 2) A collection root (no documentId):
 *    collectionPath/<actionType>/<userId>
 *
 * @param {object} db            Firestore instance
 * @param {string} collectionPath Firestore collection path (e.g., "files/library/files")
 * @param {string|undefined} documentId Optional; if provided, toggles under that doc
 * @param {string} actionType    e.g., "liked" or "favorite" (used as subcollection name)
 * @param {string} userId        Current user id
 */
function useToggleAction(db, collectionPath, documentId, actionType, userId) {
  const [state, setState] = useState(false);

  // Helper to build the subcollection ref safely
  const buildSubcollectionRef = () => {
    // Document mode
    if (documentId) {
      return collection(db, `${collectionPath}/${documentId}/${actionType}`);
    }
    // Collection mode
    return collection(db, `${collectionPath}/${actionType}`);
  };

  useEffect(() => {
    if (!userId || !actionType || !collectionPath) return;

    const subcolRef = buildSubcollectionRef();
    const userDocRef = doc(subcolRef, userId);

    (async () => {
      try {
        const snap = await getDoc(userDocRef);
        setState(snap.exists());
      } catch (error) {
        console.error(`Error fetching ${actionType} status:`, error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, collectionPath, documentId, actionType, userId]);

  const toggleAction = async () => {
    if (!userId || !actionType || !collectionPath) return;

    const subcolRef = buildSubcollectionRef();
    const userDocRef = doc(subcolRef, userId);

    try {
      if (state) {
        await deleteDoc(userDocRef);
        toast.success(
          `${actionType === 'liked' ? 'Unliked' : 'Unfavorited'} successfully!`
        );
      } else {
        await setDoc(userDocRef, {
          userId,
          timestamp: new Date(),
        });
        toast.success(
          `${actionType === 'liked' ? 'Liked' : 'Favorited'} successfully!`
        );
      }
      setState(!state);
    } catch (error) {
      console.error(`Error toggling ${actionType}:`, error);
      toast.error(`Failed to toggle ${actionType}.`);
    }
  };

  return { state, toggleAction };
}

export default useToggleAction;